import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Sprout } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      const u = res.user;
      if (u?.force_password_change) navigate("/change-password");
      else if (u?.role === "super_admin" && !u?.is_impersonating) navigate("/admin");
      else if (u?.role === "owner") navigate("/horses");
      else navigate("/dashboard");
    } else setError(res.error || "Login failed");
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="login-page">
      {/* Left visual panel */}
      <div
        className="hidden md:flex flex-col justify-between p-10"
        style={{
          background: "#3B2F1E",
          color: "#F5EFE6",
          backgroundImage:
            "radial-gradient(circle at 20% 10%, rgba(212,169,106,0.18), transparent 40%), radial-gradient(circle at 80% 90%, rgba(124,92,58,0.4), transparent 40%)",
        }}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: "#7C5C3A" }}>
            <Sprout size={24} color="#F5EFE6" />
          </div>
          <div>
            <div className="font-heading" style={{ color: "#F5EFE6", fontSize: 32, letterSpacing: "-0.02em", lineHeight: 1 }}>StablOS</div>
            <div className="text-xs mt-1" style={{ color: "#D4A96A", letterSpacing: "0.08em", textTransform: "uppercase" }}>Equine ERP</div>
          </div>
        </div>
        <div>
          <h1 className="font-heading" style={{ fontSize: 28, color: "#F5EFE6", lineHeight: 1.2 }}>
            Run your stable like a thoroughbred operation.
          </h1>
          <p className="mt-4 max-w-md" style={{ color: "#D4A96A" }}>
            From horse passports to vet appointments, transport jobs to billing — one cloud platform built for stables.
          </p>
        </div>
        <div className="text-xs" style={{ color: "rgba(245,239,230,0.5)" }}>
          © {new Date().getFullYear()} StablOS
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center px-6 py-12" style={{ background: "#F5EFE6" }}>
        <form onSubmit={submit} className="se-card w-full max-w-md" data-testid="login-form">
          <h2 className="font-heading mb-1" style={{ fontSize: 22 }}>Welcome back</h2>
          <p className="text-sm mb-6" style={{ color: "#8C9E8A" }}>
            Sign in to access your stable's dashboard.
          </p>

          <label className="se-label block mb-2">Email</label>
          <input
            className="se-input mb-4"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="login-email-input"
            placeholder="you@stable.com"
            autoComplete="email"
          />

          <label className="se-label block mb-2">Password</label>
          <input
            className="se-input mb-6"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="login-password-input"
            placeholder="••••••••"
            autoComplete="current-password"
          />

          {error && (
            <div
              className="mb-4 text-sm px-3 py-2 rounded-lg"
              style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }}
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            className="se-btn w-full mb-4"
            disabled={loading}
            data-testid="login-submit-button"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <div className="text-sm text-center" style={{ color: "#8C9E8A" }}>
            New stable?{" "}
            <Link to="/onboarding" className="se-link" data-testid="login-to-onboarding-link">
              Create your account
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
